import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ClipboardText, ArrowClockwise } from "@phosphor-icons/react";

const ACTION_LABELS = {
  LOGIN: "Connexion",
  LOGIN_FAILED: "Échec de connexion",
  USER_CREATE: "Création utilisateur",
  USER_DELETE: "Suppression utilisateur",
  USER_UPDATE: "Modification utilisateur",
  COMPANY_DELETE: "Suppression cabinet",
  COMPANY_BULK_DELETE: "Suppression multiple",
  BRANDING_UPDATE: "Modification marque",
  OUTREACH_EMAIL: "Email envoyé",
  DOCUSIGN_SEND: "Envoi signature",
};

const ACTION_TONE = {
  LOGIN: "bg-emerald-50 text-emerald-700 border-emerald-200",
  LOGIN_FAILED: "bg-red-50 text-red-700 border-red-200",
  COMPANY_DELETE: "bg-amber-50 text-amber-700 border-amber-200",
  COMPANY_BULK_DELETE: "bg-amber-50 text-amber-700 border-amber-200",
  USER_DELETE: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function AuditLog() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    if (user?.role !== "super_admin") return;
    setLoading(true);
    api.get("/audit-logs", { params: { limit: 300, ...(action ? { action } : {}) } })
      .then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [action]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user?.role !== "super_admin") return;
    api.get("/audit-logs/actions").then((r) => setActions(r.data)).catch(() => {});
  }, []);

  if (user?.role !== "super_admin") {
    return <div className="p-8 text-muted-foreground" data-testid="audit-denied">Accès réservé à l'administrateur.</div>;
  }

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR"); } catch { return iso; } };
  const detailStr = (d) => {
    if (!d) return "";
    if (typeof d === "string") return d;
    return Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join(" · ");
  };

  return (
    <div data-testid="audit-page">
      <header className="h-16 px-8 flex items-center justify-between border-b border-border bg-white sticky top-0 z-10">
        <h1 className="font-head font-bold text-xl flex items-center gap-2">
          <ClipboardText size={20} className="text-primary" /> Journal d'audit
        </h1>
        <div className="flex items-center gap-2">
          <select data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)}
            className="text-sm border border-border bg-white px-2 py-1.5 rounded outline-none focus:border-primary">
            <option value="">Toutes les actions</option>
            {actions.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
          </select>
          <button data-testid="audit-refresh" onClick={load} className="p-2 border border-border hover:bg-secondary" title="Rafraîchir">
            <ArrowClockwise size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      <div className="p-8">
        <p className="text-sm text-muted-foreground mb-4">
          Historique des connexions et actions des utilisateurs (300 dernières entrées).
        </p>
        <div className="bg-white border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-4 py-3 overline text-muted-foreground">Date & heure</th>
                <th className="px-4 py-3 overline text-muted-foreground">Utilisateur</th>
                <th className="px-4 py-3 overline text-muted-foreground">Rôle</th>
                <th className="px-4 py-3 overline text-muted-foreground">Action</th>
                <th className="px-4 py-3 overline text-muted-foreground">Cible</th>
                <th className="px-4 py-3 overline text-muted-foreground">Détails</th>
                <th className="px-4 py-3 overline text-muted-foreground">IP</th>
              </tr>
            </thead>
            <tbody data-testid="audit-rows">
              {rows.length === 0 && !loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Aucune entrée.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border hover:bg-secondary" data-testid="audit-row">
                  <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{fmt(r.ts)}</td>
                  <td className="px-4 py-2.5">{r.user_name || r.user_email || "—"}<div className="text-xs text-muted-foreground">{r.user_email}</div></td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.role || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[11px] font-semibold border px-2 py-0.5 rounded-full ${ACTION_TONE[r.action] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {ACTION_LABELS[r.action] || r.action}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs">{r.target_label || (r.target_type ? `${r.target_type}` : "—")}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[260px] truncate" title={detailStr(r.details)}>{detailStr(r.details)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{r.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
